import { Link } from "react-router-dom";
import { ArrowRight, Globe2, MapPin, Package, FileText, MessageSquare } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import CTASection from "@/components/CTASection";

const REGIONS = [
  {
    name: "India",
    tag: "Home Market",
    copy: "STJ Innovations is based in India and serves pharmaceutical manufacturers, cleanroom contractors, EPC companies, laboratories and industrial customers across the country — from specification to fabrication, delivery and installation support.",
    points: ["Direct engineering engagement", "Site-matched dimensions and panel integration", "Domestic logistics and installation coordination"],
  },
  {
    name: "GCC & Middle East",
    tag: "Focus Region",
    copy: "The Gulf region is a priority for STJ's international business development. We are building our export capability to serve customers in the UAE, Saudi Arabia and the wider GCC with engineered cleanroom equipment and precision fabrication.",
    points: ["Specification-led remote engineering", "Export-oriented documentation and packaging", "Distributor and project partnerships welcome"],
  },
  {
    name: "Africa & International",
    tag: "International",
    copy: "We welcome enquiries from international markets including Africa and Southeast Asia — from OEM fabrication programs to complete cleanroom equipment packages engineered to your project specification.",
    points: ["OEM and contract fabrication programs", "Drawings, CAD and documentation support", "English-language engineering communication"],
  },
];

const HOW = [
  [FileText, "Specification First", "Every international engagement starts with your drawings, dimensions and application requirements — quoted against a clear technical scope."],
  [Package, "Export-Ready Delivery", "Equipment is packed and documented for international shipment, with materials and finish records available for project files."],
  [MessageSquare, "Responsive Engineering Desk", "Direct communication with our engineering team across time zones — by email, video call and WhatsApp."],
];

export default function Markets() {
  return (
    <div data-testid="markets-page">
      <SEO title="Global Markets — India, GCC & International" path="/markets"
        description="STJ Innovations manufactures pharmaceutical cleanroom equipment and precision sheet-metal products in India, developing export capability for the GCC, Middle East, Africa and international B2B markets." />
      <section className="blueprint-dark relative overflow-hidden bg-ink">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(11,107,184,0.14),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Breadcrumbs dark items={[{ label: "Global Markets" }]} />
          <p className="eyebrow eyebrow-light">Global Markets</p>
          <KineticLines lines={["Made in India.", "Building for Global Markets."]}
            className="mt-5 max-w-4xl font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            STJ Innovations is developing its manufacturing and export capabilities to serve customers in India and international markets. We engage early, engineer to specification and communicate clearly — wherever your project is.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHead eyebrow="Where We Focus" title="Regions We Serve"
          copy="Country-specific pages will be added as our regional presence and project references grow — we publish only what we can stand behind." />
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {REGIONS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <article className="flex h-full flex-col border border-line bg-white p-8" data-testid={`market-region-${i}`}>
                <div className="flex items-center justify-between">
                  <MapPin size={20} className="text-accent" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">{r.tag}</span>
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-ink">{r.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{r.copy}</p>
                <ul className="mt-6 space-y-2.5 border-t border-line pt-5">
                  {r.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-[13px] text-ink">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-accent" /> {p}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="blueprint-dark bg-navy">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <SectionHead dark eyebrow="Working Internationally" title="How We Work Across Borders" />
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
            {HOW.map(([Icon, title, copy], i) => (
              <Reveal key={title} delay={i * 0.07} className="bg-navy">
                <div className="h-full p-8" data-testid={`market-how-${i}`}>
                  <Icon size={22} className="text-sky-400" />
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col gap-3 sm:flex-row">
              <Link to="/request-a-quote" data-testid="markets-rfq-button"
                className="group inline-flex items-center justify-center gap-2 bg-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
                Request a Quote <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/contact" data-testid="markets-contact-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/50">
                <Globe2 size={15} /> Discuss International Business
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
